/**
 * Export 페이지네이션 헬퍼 (공통).
 *
 * BUG-115: BE MakeList(common.js IMP-139) 가 itemperpage 를 MAX_ITEMS_PER_PAGE=200 으로
 *   silent clamp 하므로, FE 가 요청한 페이지 사이즈를 신뢰하면 안 된다.
 *   실제 반환된 list.length 를 ground truth 로 사용하여 total_page 를 계산하고,
 *   빈 페이지가 반환되면 즉시 종료한다 (totalcount 와 실제 데이터 불일치 방어).
 *
 * PR #250 review (Architecture Major): admin 디렉토리 한정에서 src/lib/export/ 로 이동.
 *   RayTableExport / ServiceInfoComments / ServiceInfoLogs 등 다른 export 경로 재사용 SSoT.
 */

/**
 * 실제 반환된 페이지 사이즈를 기준으로 전체 페이지 수를 계산한다.
 *
 * 방어:
 *  - typeof 가 number 가 아닌 값(undefined/null 등) → 0
 *  - NaN / Infinity (typeof===number 우회) → Number.isFinite 로 차단
 *  - 음수/0 → 0
 *  - 부동소수점 totalCount → Math.floor 후 계산 (BE 가 정수 외 값 반환해도 안전)
 *
 * @param {number} totalCount        BE 가 보고한 전체 건수 (response.data.data.totalcount)
 * @param {number} actualPerPage     첫 페이지 응답의 실제 list.length (BE clamp 후 값)
 * @returns {number}                 ceil(floor(totalCount) / actualPerPage). 비정상 입력은 0.
 */
export const computeTotalPages = (totalCount, actualPerPage) => {
    if (typeof totalCount !== 'number' || typeof actualPerPage !== 'number') return 0;
    if (!Number.isFinite(totalCount) || !Number.isFinite(actualPerPage)) return 0;
    if (totalCount <= 0 || actualPerPage <= 0) return 0;
    return Math.ceil(Math.floor(totalCount) / actualPerPage);
};

/**
 * Excel Formula Injection (OWASP A03 — CSV Injection) 방어.
 *
 * 셀 값이 `=`, `+`, `-`, `@`, `\t`, `\r` 로 시작하면 Excel/Google Sheets 가 수식으로
 * 해석하여 `=IMPORTDATA(...)` / `=HYPERLINK(...)` 등 임의 명령 실행 / 원격 데이터 로드가
 * 가능. prefix `'` 를 추가하여 텍스트로 강제 처리.
 *
 * 호출 위치: xlsx export 직전 (json_to_sheet 입력 row 의 string 값 모두).
 *
 * @param {*} value  원본 셀 값. string 이 아니면 변환 없이 반환.
 * @returns {*}      sanitized 값. string + 위험 prefix → `'` 추가, 그 외 → 입력 그대로.
 */
export const sanitizeCell = (value) => {
    if (typeof value !== 'string') return value;
    // PR #250 2차 review Sec Suggestion: LF(\n) 추가 — LibreOffice Calc / Google Sheets 임포트 우회 방어.
    if (/^[=+\-@\t\r\n]/.test(value)) return "'" + value;
    return value;
};

/**
 * 루프 종료 사유.
 *
 * PR #250 review (Architecture Minor): 종료 조건별 의미를 호출부에 노출하여 사용자 알림
 * UX (EMPTY_PAGE 시 경고 등) 구현 가능. 단일 boolean 반환의 shouldStop 은 후방 호환 유지.
 *
 *  - `EMPTY_PAGE` : 빈 list 반환 (totalcount/실제 데이터 불일치 — 부분 결과 종료, 사용자 경고 대상)
 *  - `NO_DATA`    : 집계 결과 없음 (totalPages <= 0)
 *  - `COMPLETED`  : 정상 마지막 페이지 도달
 *  - `null`       : 계속 루프
 *
 * @typedef {'EMPTY_PAGE' | 'NO_DATA' | 'COMPLETED' | null} StopReason
 *
 * @param {number} currentPage           현재 페이지 (1-base)
 * @param {number} totalPages            계산된 전체 페이지 수
 * @param {number} lastReturnedListLength 직전 응답의 list.length
 * @returns {StopReason}
 */
export const getStopReason = (currentPage, totalPages, lastReturnedListLength) => {
    // PR #250 2차 review CQ Minor: totalcount=0 정상 빈 결과 시 NO_DATA 우선 (EMPTY_PAGE 는 "데이터 불일치" 의미).
    if (totalPages <= 0) return 'NO_DATA';
    if (lastReturnedListLength <= 0) return 'EMPTY_PAGE';
    if (currentPage >= totalPages) return 'COMPLETED';
    return null;
};

/**
 * `getStopReason` 의 boolean wrapper (후방 호환).
 *
 * @param {number} currentPage
 * @param {number} totalPages
 * @param {number} lastReturnedListLength
 * @returns {boolean}  종료 사유가 있으면 true.
 */
export const shouldStop = (currentPage, totalPages, lastReturnedListLength) =>
    getStopReason(currentPage, totalPages, lastReturnedListLength) !== null;
